// tasksService.js
// API service for operational task management

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Tasks CRUD
// ============================================================================

/**
 * List tasks with filters
 * @param {object} params - { installerId, status, priority, assignedTo, locationId, dueBefore, dueAfter, entityType, entityId, limit, offset }
 */
export const getAll = async (params = {}) => {
    const { data } = await API.get('/api/tasks', params);
    return data.data || data;
};

/**
 * Get single task by ID
 * @param {number} taskId
 */
export const getById = async (taskId) => {
    const { data } = await API.get(`/api/tasks/${taskId}`);
    return data.data || data;
};

/**
 * Create a new task
 * @param {object} payload - { installer_id, title, description, priority, due_date, assigned_to, location_id, entity_type, entity_id, source, recurrence_rule, visibility }
 */
export const create = async (payload) => {
    const { data } = await API.post('/api/tasks', payload);
    return data.data || data;
};

/**
 * Update a task
 * @param {number} taskId
 * @param {object} payload - Fields to update
 */
export const update = async (taskId, payload) => {
    const { data } = await API.patch(`/api/tasks/${taskId}`, payload);
    return data.data || data;
};

/**
 * Complete a task
 * @param {number} taskId
 * @param {number} userId - Who completed it
 * @param {object} options - { resolution_type, completion_note }
 *   resolution_type is REQUIRED by the server for inbound_call/consultation tasks
 *   ('contact_attempted' | 'handled_no_action') — it drives GHL re-engagement automation.
 */
export const complete = async (taskId, userId, options = {}) => {
    const body = { completed_by: userId };
    if (options.resolution_type) body.resolution_type = options.resolution_type;
    if (options.completion_note) body.completion_note = options.completion_note;
    const { data } = await API.post(`/api/tasks/${taskId}/complete`, body);
    return data.data || data;
};

/**
 * Snooze a task
 * @param {number} taskId
 * @param {string} snoozeUntil - ISO date string
 */
export const snooze = async (taskId, snoozeUntil) => {
    const { data } = await API.post(`/api/tasks/${taskId}/snooze`, { snooze_until: snoozeUntil });
    return data.data || data;
};

/**
 * Cancel a task (soft-delete for non-proposed tasks)
 * @param {number} taskId
 */
export const cancel = async (taskId) => {
    const { data } = await API.post(`/api/tasks/${taskId}/cancel`);
    return data.data || data;
};

/**
 * Hard-delete a proposed task (OBD dismissals only)
 * @param {number} taskId
 */
export const deleteTask = async (taskId) => {
    const { data } = await API.delete(`/api/tasks/${taskId}`);
    return data;
};

/**
 * Tasks calendar feed — tasks with due_date in [start, end] for the in-app
 * calendar overlay (separate from the booking calendar; tasks never consume
 * booking capacity).
 * @param {object} params - { installerId, locationId, start, end, userRole, userId }
 */
export const getCalendar = async (params = {}) => {
    const { data } = await API.get('/api/tasks/calendar', params);
    return data.data || data;
};

/**
 * Get counts for badge display
 * @param {number} installerId
 * @param {object} params - Additional filters (status, locationId, etc.)
 */
export const getCounts = async (installerId, params = {}) => {
    const { data } = await API.get('/api/tasks/counts', { installerId, ...params });
    return data.data || data;
};

const TasksAPI = {
    getAll,
    getById,
    create,
    update,
    complete,
    snooze,
    cancel,
    delete: deleteTask,
    getCalendar,
    getCounts
};

export default TasksAPI;
